"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type AudiobookLabels,
  audiobookIsReady,
  AUDIOBOOK_FIELDS as F,
  formatAudiobook,
} from "@/lib/tool-kit/media-sources/audiobook-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const AUDIOBOOK_CITATION_GENERATOR_STRINGS = {
  formHeading: "Audiobook details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the book's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  authorsLabel: "Author(s)",
  authorsPlaceholder: "Harper Lee",
  authorsHint: "One author per line, as First Last or Last, First.",
  titleLabel: "Book title",
  titlePlaceholder: "To kill a mockingbird",
  titleHint:
    "Type it in sentence case, as APA wants: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  narratorsLabel: "Narrator(s)",
  narratorsPlaceholder: "Sissy Spacek",
  narratorsHint:
    "One per line. Chicago calls the narrator the reader (read by).",
  publisherLabel: "Audiobook publisher",
  publisherPlaceholder: "Caedmon",
  publisherHint:
    "The audio publisher named on the recording, such as Random House Audio or Audible Studios, not the store you bought it from.",
  yearLabel: "Year the audiobook was released",
  yearPlaceholder: "2014",
  originalYearLabel: "Year the book was first published (APA)",
  originalYearPlaceholder: "1960",
  originalYearHint:
    "For an older book, APA gives both years in the text: (Lee, 1960/2014).",
  editionLabel: "Edition",
  editionNone: "Not stated",
  editionUnabridged: "Unabridged",
  editionAbridged: "Abridged",
  durationLabel: "Running time (Chicago)",
  durationPlaceholder: "12:17",
  durationHint: "Hours and minutes, such as 12:17 or 12 hr 17 min.",
  platformLabel: "Platform you listened on (optional)",
  platformPlaceholder: "Audible",
  platformHint:
    "Audible, Libby, Spotify. MLA names it only if you give a web address; Chicago adds it to the format.",
  urlLabel: "URL (if you listened on the web)",
  urlPlaceholder: "https://www.audible.com/pd/…",
  timestampLabel: "Time stamp of a quotation (optional)",
  timestampPlaceholder: "1:25:40",
  timestampHint:
    "Where the quoted words start. It goes in the in-text citation and the notes in place of a page number.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 (§10.2) adds the narrator and [Audiobook] when the recording matters: a quotation with a time stamp, an abridged text or a point about the narration. If you only paraphrase an unabridged audiobook, you can cite the print book instead.",
  mlaNote:
    "MLA 9 credits the narrator after the title and marks the version as an audiobook edition. Quote with a time stamp in the in-text citation, as the player shows it.",
  chicagoNote:
    "Chicago 18 (§14.58) cites an audiobook like a book and adds the reader, the format and the running time. A note can point to a passage by its time: at 1:25:40.",
  example: {
    authors: "Harper Lee",
    title: "To kill a mockingbird",
    narrators: "Sissy Spacek",
    publisher: "Caedmon",
    year: "2014",
    originalYear: "1960",
    edition: "unabridged",
    duration: "12:17",
  },
} as const;

type Strings = typeof AUDIOBOOK_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    {
      id: F.narrators,
      kind: "people",
      label: strings.narratorsLabel,
      placeholder: strings.narratorsPlaceholder,
      hint: strings.narratorsHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
      hint: strings.publisherHint,
    },
    {
      id: F.year,
      kind: "text",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
    {
      id: F.originalYear,
      kind: "text",
      label: strings.originalYearLabel,
      placeholder: strings.originalYearPlaceholder,
      hint: strings.originalYearHint,
    },
    {
      id: F.edition,
      kind: "select",
      label: strings.editionLabel,
      options: [
        { value: "", label: strings.editionNone },
        { value: "unabridged", label: strings.editionUnabridged },
        { value: "abridged", label: strings.editionAbridged },
      ],
    },
    {
      id: F.duration,
      kind: "text",
      label: strings.durationLabel,
      placeholder: strings.durationPlaceholder,
      hint: strings.durationHint,
    },
    {
      id: F.timestamp,
      kind: "text",
      label: strings.timestampLabel,
      placeholder: strings.timestampPlaceholder,
      hint: strings.timestampHint,
    },
    {
      id: F.platform,
      kind: "text",
      label: strings.platformLabel,
      placeholder: strings.platformPlaceholder,
      hint: strings.platformHint,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function audiobookLabels(strings: Strings): AudiobookLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function AudiobookCitationGeneratorWidget() {
  const strings = useToolUi(AUDIOBOOK_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => audiobookLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatAudiobook(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={audiobookIsReady}
      strings={strings}
    />
  );
}
